import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { Link } from "react-router-dom";
import { fornecedoresBreadcrumbItems } from "data/fornecedores";
import {
  faPlus,
  faFilter,
  faTimes,
  faCheck,
  faTrash,
  faEye
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchBox from "components/common/SearchBox";
import { Card, Spinner, Badge } from "react-bootstrap";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import FilterOffCanvas, {
  FilterOption,
} from "components/common/FilterOffCanvas";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChangeEvent, useState, useEffect } from "react";
import FornecedoresTable, { fornecedorTableColumns } from "components/tables/FornecedoresTable";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import axios, { AxiosError } from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { keepPreviousData, useQuery } from "@tanstack/react-query";


// Interface para os filtros ativos
interface ActiveFilters {
  fornecedorStatus: string; // 'active', 'deleted', 'all'
}

// Interface para o estado de filtros consolidado
interface FilterState {
  active: ActiveFilters;
  pending: ActiveFilters | null;
  filtersPending: boolean;
}

// Interfaces baseadas no que o backend retorna
export interface FornecedorType {
  id: number;
  codigo: number;    // Adicionado código para suporte à ordenação por código
  nome?: string;
  nomeFantasia?: string;  // Adicionado nome fantasia
  razaoSocial?: string;
  cnpj?: string;
  cpf?: string;      // Adicionado CPF
  telefone?: string;
  tipoPessoa: number;
  isDeleted?: boolean; // Para suporte a itens excluídos
  deletedAt?: Date;    // Para suporte a itens excluídos
}

// Interface para representar a resposta paginada do backend
export interface PagedResponse {
  fornecedores: FornecedorType[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
}

// Função para buscar fornecedores do backend
export const fetchFornecedores = async (
  filter?: string,
  filters?: ActiveFilters,
  pageIndex: number = 1,
  pageSize: number = 10,
  incluirExcluidos: boolean = false,
  apenasExcluidos: boolean = false
): Promise<PagedResponse> => {
  // Determinar se incluir fornecedores excluídos
  const shouldIncludeDeleted =
    filters?.fornecedorStatus === "all" || filters?.fornecedorStatus === "deleted";
  // Determinar se filtrar para mostrar apenas excluídos
  const shouldShowOnlyDeleted = filters?.fornecedorStatus === "deleted";

  const response = await axios.get<PagedResponse>("/api/fornecedor", {
    params: {
      ...(filter && { filter }),
      pageIndex,
      pageSize,
      incluirExcluidos: shouldIncludeDeleted || incluirExcluidos,
      apenasExcluidos: shouldShowOnlyDeleted || apenasExcluidos
    }
  });
  return response.data;
};


const Fornecedores = () => {
  const { getQueryRetryHandler } = usePermissionModal();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showFilterCanvas, setShowFilterCanvas] = useState<boolean>(false);

  // Estado consolidado para filtros
  const [filterState, setFilterState] = useState<FilterState>({
    active: { fornecedorStatus: "active" },
    pending: null,
    filtersPending: false,
  });

  // Adicionar estado para paginação
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Opções de filtro para o componente FilterOffCanvas
  const filterOptions: FilterOption[] = [
    {
      id: "fornecedorStatus",
      label: "Status do Fornecedor",
      type: "radioCard",
      options: [
        {
          value: "active",
          label: "Ativos",
          description: "Apenas fornecedores ativos",
          icon: faCheck,
        },
        {
          value: "deleted",
          label: "Excluídos",
          description: "Apenas fornecedores excluídos",
          icon: faTrash,
        },
        {
          value: "all",
          label: "Todos",
          description: "Ativos e excluídos",
          icon: faEye,
        },
      ],
    },
  ];

  // Conta quantos filtros estão ativos
  const activeFilterCount = filterState.active.fornecedorStatus !== "active" ? 1 : 0;

  // Handler para busca
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Reset pagination when search term or filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, filterState.active]);

  const dataQuery = useQuery<PagedResponse>({
    queryKey: ["fornecedores", searchTerm, filterState.active, pagination.pageIndex, pagination.pageSize],
    queryFn: () => fetchFornecedores(
      searchTerm,
      filterState.active,
      pagination.pageIndex + 1, // +1 porque o backend espera página começando em 1, mas TanStack Table usa 0
      pagination.pageSize
    ),
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: ["fornecedores", searchTerm, filterState.active, pagination.pageIndex, pagination.pageSize],
      defaultMessage: "Você não possui permissão para consultar fornecedores."
    }),
  });

  // Explicitly refetch when pagination changes - mesmo padrão do componente Usuários
  useEffect(() => {
    if (dataQuery.data) { // Verificamos se os dados existem antes de refetch
      dataQuery.refetch();
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: dataQuery.data?.fornecedores ?? [],
    columns: fornecedorTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: dataQuery.data?.totalCount
      ? Math.ceil(dataQuery.data.totalCount / pagination.pageSize)
      : 1,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );

  if (dataQuery.error) {
    const error = dataQuery.error as AxiosError;
    if (error.response?.status === 403) {
      return (
        <div>
          <PageBreadcrumb items={fornecedoresBreadcrumbItems} />
          <div className="mb-9">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Fornecedores</h2>
              <Button
                variant="primary"
                as={Link}
                to="/cadastros/fornecedores/novo"
                className="px-4 py-2"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Novo Fornecedor
              </Button>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2 flex-grow-1">
                <SearchBox
                  placeholder="Pesquisar fornecedores"
                  onChange={() => { }}
                  className="flex-grow-1"
                  disabled={true}
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  className="px-3 py-2 d-flex align-items-center"
                  onClick={() => { }}
                  disabled={true}
                >
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filtros
                </Button>
              </div>
            </div>

            <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar fornecedores.">
              <Card className="border-0 shadow-sm" style={{
                filter: "blur(8px)",
                pointerEvents: "none",
                userSelect: "none",
              }}>
                <Card.Body className="p-0">
                  <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Código</th>
                            <th>Nome/Razão Social</th>
                            <th>CNPJ</th>
                            <th>Telefone</th>
                            <th>Cidade/UF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array(5)
                            .fill(0)
                            .map((_, i) => (
                              <tr key={i}>
                                <td>{i + 1000}</td>
                                <td>Fornecedor Exemplo Ltda</td>
                                <td>00.000.000/0001-00</td>
                                <td>(11) 9999-9999</td>
                                <td>São Paulo, SP</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </AccessDeniedOverlay>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center py-5 text-danger">
        {(dataQuery.error as Error).message}
      </div>
    );
  }

  const handleApplyFilters = (filters: Record<string, any>) => {
    // Apply filters immediately instead of storing as pending
    setFilterState((prev) => ({
      ...prev,
      active: filters as ActiveFilters,
      pending: null,
      filtersPending: false,
    }));

    // Reset to first page when applying new filters
    setPagination(prev => ({ ...prev, pageIndex: 0 }));

    // Close the filter canvas
    setShowFilterCanvas(false);

    // Refetch data with new filters
    dataQuery.refetch();
  };

  const handleClearFilters = () => {
    setFilterState((prev) => ({
      ...prev,
      active: { fornecedorStatus: "active" },
      pending: null,
      filtersPending: false,
    }));
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    dataQuery.refetch();
  };

  return (
    <div>
      <PageBreadcrumb items={fornecedoresBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Fornecedores</h2>
          <Button
            variant="primary"
            as={Link}
            to="/cadastros/fornecedores/novo"
            className="px-4 py-2"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Novo Fornecedor
          </Button>
        </div>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
              <div className="d-flex gap-2 flex-grow-1">
                <SearchBox
                  placeholder="Pesquisar fornecedores"
                  onChange={handleSearchInputChange}
                  className="flex-grow-1"
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                {activeFilterCount > 0 && (
                  <Badge
                    bg="light"
                    text="dark"
                    className="d-flex align-items-center py-2 px-3 border shadow-sm"
                    style={{ borderRadius: "50px" }}
                  >
                    <span>
                      {filterState.active.fornecedorStatus === "deleted" &&
                        "Mostrando excluídos"}
                      {filterState.active.fornecedorStatus === "all" && "Mostrando todos"}
                    </span>
                    <Button
                      variant="link"
                      className="p-0 ms-2 text-body-secondary border-0"
                      onClick={handleClearFilters}
                      style={{ fontSize: "14px" }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </Badge>
                )}

                <Button
                  variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                  className="px-3 py-2 d-flex align-items-center"
                  onClick={() => setShowFilterCanvas(true)}
                >
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filtros
                  {activeFilterCount > 0 && (
                    <Badge
                      bg="light"
                      text="primary"
                      className="ms-2 d-flex align-items-center justify-content-center"
                      style={{ borderRadius: "50%", width: "20px", height: "20px" }}
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <FornecedoresTable />
              </div>
            </Card.Body>
          </Card>
        </AdvanceTableProvider>
      </div>

      <FilterOffCanvas
        show={showFilterCanvas}
        onHide={() => setShowFilterCanvas(false)}
        onApplyFilters={handleApplyFilters}
        title="Filtros de Fornecedores"
        filterOptions={filterOptions}
        activeFilters={filterState.active}
      />
    </div>
  );
};

export default Fornecedores;